import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styles from '../../assets/styles/Admin.module.scss';
import arrowIcon from '../../assets/icons/arrow.svg';

import {
  PLATFORMS,
  KNOWLEDGE_LEVEL_MAP,
  AGENCY_NEEDS_MAP,
  HEARING_STATUS_MAP,
  ROLE_MAP,
  BUDGET_MAP
} from '../../utils/constants';
import { formatDate } from '../../utils/date';

const AdminRegistrationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isApproving, setIsApproving] = useState(false);

  // Sales Info State
  const [salesInfo, setSalesInfo] = useState({
    hearingStatus: 'UNCONTACTED',
    hearingDate: '',
    brand: '',
    monthlyBudget: '',
    platforms: [] as string[],
    knowledgeLevel: 'NONE',
    targetCTR: '',
    targetCVR: '',
    agencyNeeds: 'CONSIDERING',
    memo: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/registrations/${id}`, {
          credentials: 'include'
        });
        if (res.ok) {
          const json = await res.json();
          setData(json.user);
          if (json.hearing) {
            setSalesInfo({
              hearingStatus: json.hearing.hearing_status || 'UNCONTACTED',
              hearingDate: json.hearing.hearing_date || '',
              brand: json.hearing.brand_name || '',
              monthlyBudget: json.hearing.monthly_ad_budget?.toString() || '',
              platforms: json.hearing.used_media || [],
              knowledgeLevel: json.hearing.ad_knowledge_level || 'NONE',
              targetCTR: json.hearing.target_ctr?.toString() || '',
              targetCVR: json.hearing.target_cvr?.toString() || '',
              agencyNeeds: json.hearing.ad_agency_needs || 'CONSIDERING',
              memo: json.hearing.hearing_memo || ''
            });
          }
        }
      } catch (error) {
        console.error('Failed to fetch detail', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handleMoveToClient = async () => {
    if (!window.confirm('このユーザーを承認し、クライアントとして本登録しますか？')) return;
    try {
      setIsApproving(true);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/registrations/${id}/approve`, {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        alert('承認が完了し、クライアント管理へ移行しました。');
        setData((prev: any) => ({ ...prev, is_approved: true }));
      } else {
        const err = await res.json();
        alert(`エラーが発生しました: ${err.detail || '不明なエラー'}`);
      }
    } catch (error) {
      console.error(error);
      alert('通信エラーが発生しました。');
    } finally {
      setIsApproving(false);
    }
  };

  const handleSaveSalesInfo = async () => {
    try {
      setIsSaving(true);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/registrations/${id}/hearing`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          hearing_status: salesInfo.hearingStatus,
          hearing_date: salesInfo.hearingDate || null,
          brand_name: salesInfo.brand || null,
          monthly_ad_budget: salesInfo.monthlyBudget || null,
          ad_knowledge_level: salesInfo.knowledgeLevel,
          used_media: salesInfo.platforms,
          target_ctr: salesInfo.targetCTR ? parseFloat(salesInfo.targetCTR) : null,
          target_cvr: salesInfo.targetCVR ? parseFloat(salesInfo.targetCVR) : null,
          ad_agency_needs: salesInfo.agencyNeeds,
          hearing_memo: salesInfo.memo || null
        })
      });
      if (res.ok) {
        alert('営業情報（ヒアリング内容）を保存しました。');
      } else {
        alert('保存に失敗しました。');
      }
    } catch (error) {
      console.error(error);
      alert('通信エラーが発生しました。');
    } finally {
      setIsSaving(false);
    }
  };

  const togglePlatform = (pf: string) => {
    setSalesInfo(prev => ({
      ...prev,
      platforms: prev.platforms.includes(pf)
        ? prev.platforms.filter(p => p !== pf)
        : [...prev.platforms, pf]
    }));
  };

  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!window.confirm('この事前登録データを完全に削除しますか？\nこの操作は取り消せません。')) return;
    try {
      setIsDeleting(true);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/registrations/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (res.ok) {
        alert('アカウントを削除しました。');
        navigate('/admin/registrations');
      } else {
        const err = await res.json();
        alert(`削除に失敗しました: ${err.detail || '不明なエラー'}`);
      }
    } catch (error) {
      console.error(error);
      alert('通信エラーが発生しました。');
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) return <div className={styles.contentBox}><p>読み込み中...</p></div>;
  if (!data) return <div className={styles.contentBox}><p>データが見つかりません</p></div>;



  return (
    <div className={styles.detailWrap}>
      <div className={styles.detailMain}>
        <div className={styles.contentBox}>
          <div className={styles.regHeader}>
            <div className={styles.headerLeft}>
              <button onClick={() => navigate(-1)} className={styles.backButton}>
                <img src={arrowIcon} alt="戻る" />
              </button>
              <div className={styles.headerInfo}>
                <h1>
                  {data.company_name}
                </h1>
                <p>登録日: {formatDate(data.created_at)} | ID: {data.id}</p>
              </div>
            </div>
          </div>

          <div className={`${styles.infoBox} ${styles.infoBoxMargin}`}>
            <h2 className={`${styles.sectionTitle} ${styles.sectionTitleNoMargin}`}>基本情報</h2>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <p className={styles.infoLabel}>担当者名</p>
                <p className={`${styles.infoValue} ${styles.infoValueBold}`}>{data.last_name} {data.first_name}</p>
              </div>
              <div className={styles.infoItem}>
                <p className={styles.infoLabel}>立場</p>
                <p className={styles.infoValue}>{data.company_role ? (ROLE_MAP[data.company_role] || data.company_role) : '-'}</p>
              </div>
              <div className={styles.infoItem}>
                <p className={styles.infoLabel}>メールアドレス</p>
                <p className={styles.infoValue}>
                  {data.email}
                  <span style={{
                    marginLeft: '8px',
                    fontSize: '0.8rem',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    backgroundColor: data.is_email_verified ? '#dcfce7' : '#fee2e2',
                    color: data.is_email_verified ? '#166534' : '#991b1b'
                  }}>
                    {data.is_email_verified ? '認証済' : '未認証'}
                  </span>
                </p>
              </div>
              <div className={styles.infoItem}>
                <p className={styles.infoLabel}>電話番号</p>
                <p className={styles.infoValue}>{data.tel_num || '-'}</p>
              </div>
              <div className={`${styles.infoItem} ${styles.fullWidth}`}>
                <p className={styles.infoLabel}>申告の広告予算</p>
                <p className={styles.infoValue}>{data.monthly_ad_budget_range ? (BUDGET_MAP[data.monthly_ad_budget_range] || data.monthly_ad_budget_range) : '-'}</p>
              </div>
            </div>
          </div>

          <div className={styles.infoBox}>
            <h2 className={`${styles.sectionTitle} ${styles.sectionTitleNoMargin}`}>営業情報（ヒアリング）</h2>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <label className={styles.formLabel}>ヒアリング状況</label>
                <select
                  className={`${styles.formInput} ${styles.formSelect}`}
                  value={salesInfo.hearingStatus}
                  onChange={e => setSalesInfo({ ...salesInfo, hearingStatus: e.target.value })}
                >
                  {Object.entries(HEARING_STATUS_MAP).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>

              <div className={styles.infoItem}>
                <label className={styles.formLabel}>ヒアリング日付</label>
                <input
                  type="date"
                  className={`${styles.formInput} ${styles.dateInput}`}
                  value={salesInfo.hearingDate}
                  onChange={e => setSalesInfo({ ...salesInfo, hearingDate: e.target.value })}
                />
              </div>

              {salesInfo.hearingStatus === 'DONE' && (
                <>
                  <div className={`${styles.infoItem} ${styles.fullWidth}`}>
                    <label className={styles.formLabel}>広告運用しているブランド</label>
                    <input
                      type="text"
                      className={styles.formInput}
                      placeholder="例: コスメブランドA, SaaSサービスB"
                      value={salesInfo.brand}
                      onChange={e => setSalesInfo({ ...salesInfo, brand: e.target.value })}
                    />
                  </div>

                  <div className={styles.infoItem}>
                    <label className={styles.formLabel}>月間の広告予算</label>
                    <div className={styles.inputGroup}>
                      <input
                        type="text"
                        className={styles.formInput}
                        placeholder="例: 1000000"
                        value={salesInfo.monthlyBudget}
                        onChange={e => setSalesInfo({ ...salesInfo, monthlyBudget: e.target.value })}
                      />
                      <span className={styles.inputSuffix}>円</span>
                    </div>
                  </div>

                  <div className={styles.infoItem}>
                    <label className={styles.formLabel}>広告運用知識</label>
                    <select
                      className={`${styles.formInput} ${styles.formSelect}`}
                      value={salesInfo.knowledgeLevel}
                      onChange={e => setSalesInfo({ ...salesInfo, knowledgeLevel: e.target.value })}
                    >
                      {Object.entries(KNOWLEDGE_LEVEL_MAP).map(([key, label]) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </select>
                  </div>

                  <div className={`${styles.infoItem} ${styles.fullWidth}`}>
                    <label className={styles.formLabel}>利用している媒体（複数選択）</label>
                    <div className={styles.checkboxGrid}>
                      {PLATFORMS.map(pf => (
                        <label key={pf.value} className={styles.checkboxItem}>
                          <input
                            type="checkbox"
                            checked={salesInfo.platforms.includes(pf.label)}
                            onChange={() => togglePlatform(pf.label)}
                          />
                          <span>{pf.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className={styles.infoItem}>
                    <label className={styles.formLabel}>目標CTR</label>
                    <div className={styles.inputGroup}>
                      <input
                        type="text"
                        className={styles.formInput}
                        placeholder="例: 1.5"
                        value={salesInfo.targetCTR}
                        onChange={e => setSalesInfo({ ...salesInfo, targetCTR: e.target.value })}
                      />
                      <span className={styles.inputSuffix}>%</span>
                    </div>
                  </div>

                  <div className={styles.infoItem}>
                    <label className={styles.formLabel}>目標CVR</label>
                    <div className={styles.inputGroup}>
                      <input
                        type="text"
                        className={styles.formInput}
                        placeholder="例: 2.0"
                        value={salesInfo.targetCVR}
                        onChange={e => setSalesInfo({ ...salesInfo, targetCVR: e.target.value })}
                      />
                      <span className={styles.inputSuffix}>%</span>
                    </div>
                  </div>

                  <div className={`${styles.infoItem} ${styles.fullWidth}`}>
                    <label className={styles.formLabel}>広告運用代行ニーズ</label>
                    <select
                      className={`${styles.formInput} ${styles.formSelect}`}
                      value={salesInfo.agencyNeeds}
                      onChange={e => setSalesInfo({ ...salesInfo, agencyNeeds: e.target.value })}
                    >
                      {Object.entries(AGENCY_NEEDS_MAP).map(([key, label]) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              <div className={`${styles.infoItem} ${styles.fullWidth}`}>
                <label className={styles.formLabel}>備考（ヒアリングメモ）</label>
                <textarea
                  className={styles.textArea}
                  placeholder="顧客の課題や要望、ネクストアクションなどを自由に入力してください"
                  value={salesInfo.memo}
                  onChange={e => setSalesInfo({ ...salesInfo, memo: e.target.value })}
                ></textarea>
              </div>

              <div className={`${styles.infoItem} ${styles.fullWidth} ${styles.submitAction}`}>
                <button
                  className={styles.actionButton}
                  onClick={handleSaveSalesInfo}
                  disabled={isSaving}
                >
                  {isSaving ? '保存中...' : '営業情報を保存する'}
                </button>
              </div>
            </div>
          </div>
        </div> {/* End of regDetailWrap */}
      </div> {/* End of detailMain */}

      {/* Right Sidebar */}
      <div className={styles.detailSidebar}>
        <div className={styles.contentBox}>
          <h2 className={styles.sectionTitle}>アクション</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <button
              onClick={handleMoveToClient}
              disabled={data.is_approved || isApproving || isDeleting}
              className={`${styles.actionButton} ${data.is_approved ? styles.disabled : ''}`}
              style={{ width: '100%', justifyContent: 'center' }}
            >
              {isApproving ? '処理中...' : data.is_approved ? '移行済み' : 'クライアント管理へ移行'}
            </button>
            <button
              onClick={handleDelete}
              disabled={isDeleting || isApproving}
              className={styles.deleteButton}
              style={{ width: '100%', backgroundColor: '#ef4444', color: 'white', border: 'none', padding: '12px 20px', borderRadius: '4px', cursor: 'pointer', opacity: isDeleting ? 0.6 : 1, fontSize: '14px', fontWeight: 'bold' }}
            >
              {isDeleting ? '削除中...' : 'アカウントを削除'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminRegistrationDetail;
